import { useState } from "react";
import { Menu } from "lucide-react";
import { Link, NavLink } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";


const navItems = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About the SLP" },
  { to: "/modules", label: "Modules" },
  { to: "/contact", label: "Contact Us" },
];


export const PublicShell = ({ children, publicConfig, session, onLogout }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const headerPrimaryLogoUrl = "/branding/zallywood-logo.png";
  const headerPartnerLogoUrl = "/branding/unisa-logo.png";
  const supportEmail = publicConfig?.support_email || "—";
  const proofEmail = publicConfig?.banking_details?.proof_of_payment_email || supportEmail;
  const bankName = publicConfig?.banking_details?.bank_name || "—";
  const accountName = publicConfig?.banking_details?.account_name || "—";
  const accountNumber = publicConfig?.banking_details?.account_number || "—";
  const branchCode = publicConfig?.banking_details?.branch_code || "—";
  const brand = publicConfig?.brand?.colors || {
    primary_dark: "#2B2B2B",
    secondary_dark: "#3A3A3A",
    primary_orange: "#F5A623",
    white: "#FFFFFF",
    offwhite: "#F7F5F1",
  };

  const closeMobileMenu = () => setMobileMenuOpen(false);

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top_left,_rgba(245,166,35,0.12),_transparent_30%),linear-gradient(180deg,#f7f5f1_0%,#f4f1ea_52%,#faf8f4_100%)] text-slate-900">
      <header className="sticky top-0 z-40 border-b border-stone-200/80 bg-[#f7f5f1]/96 backdrop-blur-xl">
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-4 py-4 xl:gap-5">
            <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
              <div className="flex min-w-0 flex-1 flex-col gap-3 xl:flex-row xl:items-center xl:gap-3.5">
                <Link to="/" className="block min-w-0" data-testid="site-logo-link">
                  <div className="flex min-w-0 items-center gap-3 rounded-[1.35rem] border border-black/10 px-3 py-3 shadow-sm sm:h-[5.15rem] sm:gap-4 sm:px-4" style={{ backgroundColor: brand.primary_dark }}>
                    <div className="flex h-14 w-[5.8rem] shrink-0 items-center justify-center overflow-hidden rounded-lg sm:h-[4.5rem] sm:w-[7.9rem]" data-testid="header-primary-logo-wrap">
                      <img src={headerPrimaryLogoUrl} alt="Zallywood logo" className="h-full w-full object-contain" data-testid="header-primary-logo" />
                    </div>
                    <div className="min-w-0 space-y-1">
                      <p className="text-[1.05rem] font-semibold leading-tight tracking-[-0.018em] text-white sm:text-[1.24rem]" data-testid="site-title-text">
                        Theology Portal
                      </p>
                      <p className="text-[0.68rem] uppercase leading-5 tracking-[0.16em] text-white/75 sm:text-[0.76rem]" data-testid="site-tagline-text">
                        Admissions • Student Onboarding
                      </p>
                    </div>
                  </div>
                </Link>

                <div className="flex min-w-0 items-center gap-3 rounded-[1.35rem] border border-black/10 bg-white px-3 py-3 shadow-sm sm:h-[5.15rem] sm:gap-4 sm:px-4 xl:min-w-[18rem] xl:max-w-[18.5rem]" data-testid="header-partner-logo-wrap">
                  <div className="flex h-14 w-[5.8rem] shrink-0 items-center justify-center overflow-hidden rounded-lg bg-white sm:h-[4.5rem] sm:w-[8.1rem]">
                    <img src={headerPartnerLogoUrl} alt="UNISA logo" className="h-auto w-full object-contain" data-testid="header-partner-logo" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[0.68rem] font-semibold uppercase tracking-[0.16em] text-slate-500 sm:text-[0.78rem] sm:tracking-[0.18em]" data-testid="header-partner-label">Institutional Partner</p>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2 pt-0.5 sm:gap-2.5 xl:justify-end">
                {session?.role === "student" && (
                  <Button asChild variant="outline" className="min-h-11 rounded-full bg-transparent px-4 text-sm" style={{ borderColor: brand.primary_orange, color: brand.primary_dark }} data-testid="header-student-portal-button">
                    <Link to="/student">Student portal</Link>
                  </Button>
                )}
                {["super_admin", "admin"].includes(session?.role) && (
                  <Button asChild variant="outline" className="min-h-11 rounded-full bg-transparent px-4 text-sm" style={{ borderColor: brand.primary_orange, color: brand.primary_dark }} data-testid="header-admin-portal-button">
                    <Link to="/admin/dashboard">Admin dashboard</Link>
                  </Button>
                )}
                {session ? (
                  <Button className="min-h-11 rounded-full px-4 text-sm" style={{ backgroundColor: brand.primary_dark, color: brand.white }} data-testid="header-logout-button" onClick={onLogout}>
                    Sign out
                  </Button>
                ) : (
                  <>
                    <Button asChild className="hidden min-h-11 rounded-full px-4 text-sm xl:inline-flex" style={{ backgroundColor: brand.primary_orange, color: brand.primary_dark }} data-testid="header-apply-button">
                      <Link to="/apply">Apply now</Link>
                    </Button>
                    <Button asChild variant="outline" className="hidden min-h-11 rounded-full bg-transparent px-4 text-sm xl:inline-flex" style={{ borderColor: brand.primary_orange, color: brand.primary_dark }} data-testid="header-login-button">
                      <Link to="/login">Student login</Link>
                    </Button>
                  </>
                )}
                <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                  <SheetTrigger asChild>
                    <Button variant="outline" className="min-h-11 rounded-full bg-white/85 px-4 xl:hidden" style={{ borderColor: brand.primary_orange, color: brand.primary_dark }} data-testid="mobile-nav-trigger-button">
                      <Menu className="h-4 w-4" />
                      Menu
                    </Button>
                  </SheetTrigger>
                  <SheetContent side="right" className="border-stone-200 bg-[#f7f5f1] px-5" data-testid="mobile-nav-sheet">
                    <SheetHeader className="space-y-2 text-left">
                      <SheetTitle className="text-xl tracking-[-0.02em] text-slate-950" data-testid="mobile-nav-title">Navigation</SheetTitle>
                      <SheetDescription className="text-sm leading-6 text-slate-600" data-testid="mobile-nav-description">
                        Browse the theology portal, application pages, and support links on mobile.
                      </SheetDescription>
                    </SheetHeader>
                    <div className="mt-8 space-y-3" data-testid="mobile-nav-links">
                      {navItems.map((item) => (
                        <NavLink
                          key={item.to}
                          to={item.to}
                          onClick={closeMobileMenu}
                          data-testid={`mobile-nav-link-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                          className={({ isActive }) => `flex min-h-12 items-center justify-between rounded-[1.15rem] border px-4 py-3 text-sm font-semibold uppercase tracking-[0.12em] transition-colors ${isActive ? "border-[#d9b067] bg-white text-slate-950" : "border-stone-200 bg-[#fcfaf5] text-slate-700 hover:bg-white"}`}
                        >
                          {item.label}
                        </NavLink>
                      ))}
                    </div>
                    {!session && (
                      <div className="mt-8 grid gap-3" data-testid="mobile-nav-actions">
                        <Link
                          to="/apply"
                          onClick={closeMobileMenu}
                          className="inline-flex min-h-12 items-center justify-center rounded-full px-4 text-sm font-medium"
                          style={{ backgroundColor: brand.primary_orange, color: brand.primary_dark }}
                          data-testid="mobile-nav-apply-button"
                        >
                          Apply now
                        </Link>
                        <Link
                          to="/login"
                          onClick={closeMobileMenu}
                          className="inline-flex min-h-12 items-center justify-center rounded-full border bg-transparent px-4 text-sm font-medium"
                          style={{ borderColor: brand.primary_orange, color: brand.primary_dark }}
                          data-testid="mobile-nav-login-button"
                        >
                          Student login
                        </Link>
                      </div>
                    )}
                  </SheetContent>
                </Sheet>
              </div>
            </div>

            <div className="hidden items-center gap-10 border-t border-black/8 py-3.5 xl:flex">
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  data-testid={`nav-link-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className={({ isActive }) => `whitespace-nowrap text-[15px] font-bold uppercase tracking-[0.12em] transition-colors ${isActive ? "text-slate-950" : "text-slate-700 hover:text-slate-950"}`}
                  style={({ isActive }) => ({ color: isActive ? brand.primary_orange : undefined })}
                >
                  {item.label}
                </NavLink>
              ))}
            </div>
          </div>
        </div>
      </header>

      <main>{children}</main>

      <footer className="border-t border-black/10" style={{ backgroundColor: brand.primary_dark, color: brand.white }}>
        <div className="mx-auto grid w-full max-w-7xl gap-10 px-4 py-14 sm:px-6 lg:grid-cols-[1.25fr_1fr_1fr_1fr] lg:px-8">
          <div className="space-y-6">
            <div className="flex flex-wrap items-center gap-3" data-testid="footer-brand-block">
              <div className="flex h-16 w-20 items-center justify-center overflow-hidden rounded-2xl" style={{ backgroundColor: brand.secondary_dark }}>
                <img src={headerPrimaryLogoUrl} alt="Zallywood logo" className="h-full w-full object-contain p-1" data-testid="footer-primary-logo" />
              </div>
              <div className="flex h-14 w-20 items-center justify-center overflow-hidden rounded-2xl bg-white px-2 py-1">
                <img src={headerPartnerLogoUrl} alt="UNISA logo" className="h-full w-full object-contain" data-testid="footer-partner-logo" />
              </div>
            </div>
            <p className="text-[1.85rem] font-semibold leading-[1.02] tracking-[-0.02em] text-white" data-testid="footer-portal-name-text">
              Theology Portal
            </p>
            <p className="text-sm uppercase tracking-[0.16em] text-white/62" data-testid="footer-portal-subtitle-text">
              {publicConfig?.portal_name || "Zallywood x UNISA Theology SLP Portal"}
            </p>
            <p className="max-w-lg text-sm leading-7 text-white/72" data-testid="footer-summary-text">
              A formal application and student-access portal for Zallywood x UNISA Theology Short Learning Programmes, offering a secure application process, guided onboarding, and clear payment communication.
            </p>
          </div>

          <div className="space-y-3">
            <p className="text-xs font-semibold uppercase tracking-[0.24em]" style={{ color: brand.primary_orange }} data-testid="footer-process-heading">
              Admissions & payment
            </p>
            <p className="text-sm text-white/80" data-testid="footer-process-item-1">Apply online and save your student number.</p>
            <p className="text-sm text-white/80" data-testid="footer-process-item-2">Use it as your EFT payment reference.</p>
            <p className="text-sm text-white/80" data-testid="footer-process-item-3">Email proof of payment to {proofEmail}.</p>
            <p className="text-sm text-white/80" data-testid="footer-process-item-4">Wait for approval and your setup / access guidance.</p>
          </div>

          <div className="space-y-3">
            <p className="text-xs font-semibold uppercase tracking-[0.24em]" style={{ color: brand.primary_orange }} data-testid="footer-banking-heading">
              EFT details
            </p>
            <p className="text-sm text-white/80" data-testid="footer-bank-name-text">Bank: {bankName}</p>
            <p className="text-sm text-white/80" data-testid="footer-account-name-text">Account: {accountName}</p>
            <p className="text-sm text-white/80" data-testid="footer-account-number-text">Account no: {accountNumber}</p>
            <p className="text-sm text-white/80" data-testid="footer-branch-code-text">Branch code: {branchCode}</p>
          </div>

          <div className="space-y-3">
            <p className="text-xs font-semibold uppercase tracking-[0.24em]" style={{ color: brand.primary_orange }} data-testid="footer-support-heading">
              Contact & legal
            </p>
            <p className="break-words text-sm text-white/80" data-testid="footer-support-email-text">Support: {supportEmail}</p>
            <p className="break-words text-sm text-white/80" data-testid="footer-proof-email-text">Proof of payment: {proofEmail}</p>
            <div className="flex flex-col gap-2 text-sm text-white/80">
              <Link to="/privacy" data-testid="footer-privacy-link">Privacy policy</Link>
              <Link to="/terms" data-testid="footer-terms-link">Terms & disclaimer</Link>
              <Link to="/contact" data-testid="footer-contact-link">Contact Us</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};